/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {scheduleMicroTask} from '../util';
import {AnimationPlayer} from './animation_player';
export class AnimationGroupPlayer implements AnimationPlayer {
private _onDoneFns: Function[] = [];
private _onStartFns: Function[] = [];
private _finished = false;
private _started = false;
private _destroyed = false;
private _onDestroyFns: Function[] = [];
public parentPlayer: AnimationPlayer|null = null;
public totalTime: number = 0;
/**
 * @param {?} _players
 */
constructor(private _players: AnimationPlayer[]) {
    let doneCount = 0;
    let destroyCount = 0;
    let startCount = 0;
    const total = this._players.length;

    if (total == 0) {
      scheduleMicroTask(() => this._onFinish());
    } else {
      this._players.forEach(player => {
        player.parentPlayer = this;
        player.onDone(() => {
          if (++doneCount >= total) {
            this._onFinish();
          }
        });
        player.onDestroy(() => {
          if (++destroyCount >= total) {
            this._onDestroy();
          }
        });
        player.onStart(() => {
          if (++startCount >= total) {
            this._onStart();
          }
        });
      });
    }

    this.totalTime = this._players.reduce((time, player) => Math.max(time, player.totalTime), 0);
  }
/**
 * @return {?}
 */
private _onFinish() {
    if (!this._finished) {
      this._finished = true;
      this._onDoneFns.forEach(fn => fn());
      this._onDoneFns = [];
    }
  }
/**
 * @return {?}
 */
init(): void { this._players.forEach(player => player.init()); }
/**
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void): void { this._onStartFns.push(fn); }
/**
 * @return {?}
 */
private _onStart() {
    if (!this.hasStarted()) {
      this._onStartFns.forEach(fn => fn());
      this._onStartFns = [];
      this._started = true;
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void): void { this._onDoneFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void): void { this._onDestroyFns.push(fn); }
/**
 * @return {?}
 */
hasStarted() { return this._started; }
/**
 * @return {?}
 */
play() {
    if (!this.parentPlayer) {
      this.init();
    }
    this._onStart();
    this._players.forEach(player => player.play());
  }
/**
 * @return {?}
 */
pause(): void { this._players.forEach(player => player.pause()); }
/**
 * @return {?}
 */
restart(): void { this._players.forEach(player => player.restart()); }
/**
 * @return {?}
 */
finish(): void {
    this._onFinish();
    this._players.forEach(player => player.finish());
  }
/**
 * @return {?}
 */
destroy(): void { this._onDestroy(); }
/**
 * @return {?}
 */
private _onDestroy() {
    if (!this._destroyed) {
      this._destroyed = true;
      this._onFinish();
      this._players.forEach(player => player.destroy());
      this._onDestroyFns.forEach(fn => fn());
      this._onDestroyFns = [];
    }
  }
/**
 * @return {?}
 */
reset(): void {
    this._players.forEach(player => player.reset());
    this._destroyed = false;
    this._finished = false;
    this._started = false;
  }
/**
 * @param {?} p
 * @return {?}
 */
setPosition(p: number): void {
    const /** @type {?} */ timeAtPosition = p * this.totalTime;
    this._players.forEach(player => {
      const /** @type {?} */ position = player.totalTime ? Math.min(1, timeAtPosition / player.totalTime) : 1;
      player.setPosition(position);
    });
  }
/**
 * @return {?}
 */
getPosition(): number {
    let /** @type {?} */ min = 0;
    this._players.forEach(player => {
      const /** @type {?} */ p = player.getPosition();
      min = Math.min(p, min);
    });
    return min;
  }
/**
 * @return {?}
 */
get players(): AnimationPlayer[] { return this._players; }
/**
 * @return {?}
 */
beforeDestroy(): void {
    this.players.forEach(player => {
      if (player.beforeDestroy) {
        player.beforeDestroy();
      }
    });
  }
}

function AnimationGroupPlayer_tsickle_Closure_declarations() {
/** @type {?} */
AnimationGroupPlayer.prototype._onDoneFns;
/** @type {?} */
AnimationGroupPlayer.prototype._onStartFns;
/** @type {?} */
AnimationGroupPlayer.prototype._finished;
/** @type {?} */
AnimationGroupPlayer.prototype._started;
/** @type {?} */
AnimationGroupPlayer.prototype._destroyed;
/** @type {?} */
AnimationGroupPlayer.prototype._onDestroyFns;
/** @type {?} */
AnimationGroupPlayer.prototype.parentPlayer;
/** @type {?} */
AnimationGroupPlayer.prototype.totalTime;
/** @type {?} */
AnimationGroupPlayer.prototype._players;
}

